import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { API_STATUS } from "../../constants";
import { GetInstagramMedia } from "../../apis/posts/get_instagram_media";

export default function InstagramMedia({ onSelectMedia }) {
    const navigate = useNavigate();
    const [mediaList, setMediaList] = useState([]);
    const [selectedMedia, setSelectedMedia] = useState(null);
    const [mediaApi, setMediaApi] = useState(API_STATUS);
    const [instaLogin, setInstaLogin] = useState(true);
    const [activeTab, setActiveTab] = useState("videos"); // "photos" | "videos"

    // Pagination State
    const [pagination, setPagination] = useState({
        has_next: false,
        after: null,
    });
    const [isLoadingMore, setIsLoadingMore] = useState(false);

    // handle instagram auth
    const handleInstaAuth = (url) => {
        window.location.href = url;
    };

    // Initial fetch
    useEffect(() => {
        if (!instaLogin) return;
        setMediaList([]);
        setSelectedMedia(null);
        setPagination({ has_next: false, after: null });

        GetInstagramMedia(setMediaApi);
    }, [instaLogin]);

    // Handle API Response
    useEffect(() => {
        if (!mediaApi.isLoader && !mediaApi.isError) {
            if (mediaApi.data != null) {
                const res = mediaApi.data;
                if (res["status"] === 2) {
                    handleInstaAuth(res["auth_url"]);
                }
                if (res["status"] === 1 && Array.isArray(res["data"])) {
                    if (isLoadingMore) {
                        setMediaList((prev) => [...prev, ...res["data"]]);
                        setIsLoadingMore(false);
                    } else {
                        setMediaList(res["data"]);
                    }

                    if (res["pagination"]) {
                        setPagination({
                            has_next: res["pagination"]["has_next"] || false,
                            after: res["pagination"]["after"] || null,
                        });
                    }
                }
            }
        }
    }, [mediaApi]);

    // Load next page
    const handleLoadMore = () => {
        if (pagination.has_next && pagination.after) {
            setIsLoadingMore(true);
            GetInstagramMedia(setMediaApi, pagination.after);
        }
    };

    // Single item selection handler
    const handleSelect = (item) => {
        const newSelection = selectedMedia?.id === item.id ? null : item;
        setSelectedMedia(newSelection);
        if (onSelectMedia) {
            onSelectMedia(newSelection ? [newSelection] : []);
        }
    };

    // Filter media by active tab
    const filteredMedia = mediaList.filter((item) => {
        const type = (item.media_type || item.file_type || "").toUpperCase();
        if (activeTab === "videos") {
            return type === "VIDEO" || type === "VIDEOS";
        }
        return type === "IMAGE" || type === "CAROUSEL_ALBUM" || type === "PHOTOS" || type === "PHOTO";
    });

    return (
        <div className="w-full">
            {/* Tabs Bar */}
            <div className="flex border-b border-gray-200 dark:border-gray-700 mb-4">
                <button
                    type="button"
                    onClick={() => setActiveTab("videos")}
                    className={`flex-1 py-2.5 text-center font-medium text-sm transition border-b-2 ${activeTab === "videos"
                        ? "border-blue-600 text-blue-600 dark:text-blue-400"
                        : "border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400"
                        }`}
                >
                    Videos
                </button>

                <button
                    type="button"
                    onClick={() => setActiveTab("photos")}
                    className={`flex-1 py-2.5 text-center font-medium text-sm transition border-b-2 ${activeTab === "photos"
                        ? "border-blue-600 text-blue-600 dark:text-blue-400"
                        : "border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400"
                        }`}
                >
                    Photos
                </button>
            </div>

            {/* Loading Spinner */}
            {mediaApi.isLoader && !isLoadingMore && (
                <div className="flex justify-center items-center py-12">
                    <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                </div>
            )}

            {/* Empty State */}
            {!mediaApi.isLoader && filteredMedia.length === 0 && (
                <div className="text-center py-10 text-gray-500 text-sm">
                    No {activeTab} found.
                </div>
            )}

            {/* Media Grid */}
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2 max-h-[450px] overflow-y-auto p-1">
                {filteredMedia.map((item) => {
                    const isSelected = selectedMedia?.id === item.id;
                    const isVideo = (item.media_type || item.file_type || "").toUpperCase().includes("VIDEO");
                    const thumbUrl = item.thumbnail_url || item.media_url || item.file_path || item.thumbnail;

                    return (
                        <div
                            key={item.id || item.file_id}
                            onClick={() => handleSelect(item)}
                            className={`relative aspect-square rounded-lg overflow-hidden cursor-pointer border-2 transition group ${isSelected
                                ? "border-blue-600 ring-2 ring-blue-600/30"
                                : "border-transparent hover:border-gray-300"
                                }`}
                        >
                            <img
                                src={thumbUrl}
                                alt="Instagram Media"
                                className="w-full h-full object-cover"
                                loading="lazy"
                            />

                            {/* Video Badge Icon */}
                            {isVideo && (
                                <div className="absolute top-1.5 right-1.5 bg-black/60 text-white p-1 rounded-full backdrop-blur-sm z-10">
                                    <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                                        <path d="M2 6a2 2 0 012-2h6a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V6zM14.553 7.106A1 1 0 0014 8v4a1 1 0 001.553.832l3-2a1 1 0 000-1.664l-3-2z" />
                                    </svg>
                                </div>
                            )}

                            {/* Selected Checkmark Overlay */}
                            {isSelected && (
                                <div className="absolute inset-0 bg-blue-600/20 flex items-center justify-center z-10">
                                    <div className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center shadow">
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                        </svg>
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Load More Button */}
            {pagination.has_next && (
                <div className="mt-4 flex justify-center">
                    <button
                        type="button"
                        onClick={handleLoadMore}
                        disabled={isLoadingMore}
                        className="px-4 py-1.5 text-xs font-medium text-blue-600 border border-blue-600 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-950/30 transition disabled:opacity-50"
                    >
                        {isLoadingMore ? "Loading..." : "Load More"}
                    </button>
                </div>
            )}
        </div>
    );
}